import { DateTime, T, Var } from 'gt-react';
import {
  splitCostSourceClause,
  type RationaleCostAgreement,
} from '@erp/shared-types';
import { parseDateOnlyLocal } from '@erp/shared-utils';
import { humanizeRationale } from './rationaleLabels';

/**
 * An option's rationale as the board shows it. The engine writes a reason
 * (a code such as `no_surplus_buy`, or the forward-buy pass's sentence) and,
 * when a supplier cost agreement priced the buy, appends the contract that did
 * (docs/procurement/forward-buying.md, Part 2, decision 17):
 * "no_surplus_buy; priced by contract 4046, ends 2026-09-30". The reason is
 * humanized as before; the contract clause is rendered in the buyer's own
 * words and date format: "priced by contract 4046, ends Sep 30, 2026".
 */

const ENDS_ON_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

function CostSourceClause({
  agreement,
}: {
  agreement: RationaleCostAgreement;
}) {
  const endsOn =
    agreement.constraintEndsOn === null
      ? null
      : parseDateOnlyLocal(agreement.constraintEndsOn);
  if (agreement.contractNumber === null) {
    return (
      <T context="replenishment option rationale, after the reason: a supplier cost agreement priced this buy; its contract number is unknown">
        priced by a contract
      </T>
    );
  }
  if (endsOn === null) {
    return (
      <T context="replenishment option rationale, after the reason: the supplier contract number that priced this buy">
        priced by contract <Var>{String(agreement.contractNumber)}</Var>
      </T>
    );
  }
  return (
    <T context="replenishment option rationale, after the reason: the supplier contract number that priced this buy, and the last day that contract is valid">
      priced by contract <Var>{String(agreement.contractNumber)}</Var>, ends{' '}
      <DateTime options={ENDS_ON_FORMAT}>{endsOn}</DateTime>
    </T>
  );
}

export function OptionRationale({ rationale }: { rationale: string }) {
  const { reason, agreement } = splitCostSourceClause(rationale);
  const humanized = humanizeRationale(reason);
  if (agreement === null) return <>{humanized}</>;
  return (
    <>
      {humanized}; <CostSourceClause agreement={agreement} />
    </>
  );
}
